import {
  reportService,
  type CustomerPurchaseDetailResponse,
  type InventoryItemType,
  type InventoryTrackingEvent,
} from '../../../services/report.service';
import { mapWithConcurrency } from './concurrency';

const CONCURRENCY = 5;

export interface InventoryTrackingGather {
  tracking: Map<string, InventoryTrackingEvent[]>; // only items with >=1 event
  failed: number;
}

export async function gatherInventoryTracking(
  items: ReadonlyArray<{ itemId: string }>,
  itemType: InventoryItemType
): Promise<InventoryTrackingGather> {
  const results = await mapWithConcurrency(items, CONCURRENCY, async (item) => {
    try {
      const res = await reportService.getInventoryTracking(item.itemId, itemType);
      return { itemId: item.itemId, events: res.events, ok: true as const };
    } catch {
      return { itemId: item.itemId, events: [] as InventoryTrackingEvent[], ok: false as const };
    }
  });
  const tracking = new Map<string, InventoryTrackingEvent[]>();
  let failed = 0;
  for (const r of results) {
    if (!r.ok) { failed += 1; continue; }
    if (r.events.length > 0) tracking.set(r.itemId, r.events);
  }
  return { tracking, failed };
}

export interface CustomerDetailGather {
  details: Map<string, CustomerPurchaseDetailResponse>;
  failed: number;
}

export async function gatherCustomerDetails(
  customers: ReadonlyArray<{ customerId: string }>,
  params: { startDate?: string; endDate?: string }
): Promise<CustomerDetailGather> {
  const results = await mapWithConcurrency(customers, CONCURRENCY, async (c) => {
    try {
      const detail = await reportService.getCustomerPurchaseDetail(c.customerId, params);
      return { customerId: c.customerId, detail, ok: true as const };
    } catch {
      return { customerId: c.customerId, detail: null, ok: false as const };
    }
  });
  const details = new Map<string, CustomerPurchaseDetailResponse>();
  let failed = 0;
  for (const r of results) {
    if (!r.ok || !r.detail) { failed += 1; continue; }
    details.set(r.customerId, r.detail);
  }
  return { details, failed };
}
